/**
 * ClientAuthContext — V5-T9
 * Context d'authentification isolé pour le portail client.
 * NE partage AUCUN état avec AuthContext.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

const CLIENT_TOKEN_KEY = "flowbtp_client_token";

function decodeJwtPayload(token) {
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

function isTokenValid(token) {
  if (!token) return false;
  const payload = decodeJwtPayload(token);
  if (!payload) return false;
  if (payload.role !== "client" || payload.type !== "client_access") return false;
  return Date.now() < payload.exp * 1000;
}

const ClientAuthContext = createContext(null);

export function ClientAuthProvider({ children }) {
  const [token, setToken]   = useState(() => localStorage.getItem(CLIENT_TOKEN_KEY));
  const [client, setClient] = useState(() => {
    const t = localStorage.getItem(CLIENT_TOKEN_KEY);
    if (!isTokenValid(t)) return null;
    return decodeJwtPayload(t);
  });
  const [loading, setLoading] = useState(false);

  const isAuthenticated = isTokenValid(token) && !!client;

  const _setAuth = useCallback((rawToken, clientInfo) => {
    localStorage.setItem(CLIENT_TOKEN_KEY, rawToken);
    setToken(rawToken);
    setClient(clientInfo ?? decodeJwtPayload(rawToken));
  }, []);

  const clientLogout = useCallback(() => {
    localStorage.removeItem(CLIENT_TOKEN_KEY);
    setToken(null);
    setClient(null);
    api.post("/client/auth/logout").catch(() => {});
  }, []);

  const clientLogin = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const res = await api.post("/client/auth/login", { email, password });
      _setAuth(res.data.access_token, res.data.client);
      return res.data;
    } finally {
      setLoading(false);
    }
  }, [_setAuth]);

  const acceptInvite = useCallback(async (inviteToken, password) => {
    setLoading(true);
    try {
      const res = await api.post("/client/auth/accept-invite", { token: inviteToken, password });
      _setAuth(res.data.access_token, res.data.client);
      return res.data;
    } finally {
      setLoading(false);
    }
  }, [_setAuth]);

  useEffect(() => {
    window.__CLIENT_TOKEN__ = (token && isTokenValid(token)) ? token : null;
  }, [token]);

  const value = {
    isAuthenticated, client, token, loading,
    clientLogin, clientLogout, acceptInvite,
    siteIds: client?.site_ids || [],
  };

  return (
    <ClientAuthContext.Provider value={value}>
      {children}
    </ClientAuthContext.Provider>
  );
}

export function useClientAuth() {
  const ctx = useContext(ClientAuthContext);
  if (!ctx) throw new Error("useClientAuth doit être utilisé dans ClientAuthProvider");
  return ctx;
}

export default ClientAuthContext;
